import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Check,
  ChevronDown,
  LayoutDashboard,
  ShoppingBag,
  Store,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";
import { GlassPanel } from "../GlassPanel/GlassPanel";
import { useMenuPopover } from "./useMenuPopover";

/**
 * El conmutador de VISTAS del super-admin (Anahí, 27-ago): un dropdown
 * en la barra del panel para saltar entre el panel de la casa, el del
 * socio, el del franquiciatario y la tienda como cliente — la
 * referencia traía dos botones; con cuatro vistas, un menú.
 *
 * Es NAVEGACIÓN, no permisos: solo se pinta para `super_admin` (quien
 * puede verlo todo) y cada panel conserva sus guardas. Cambiar de vista
 * no cambia quién eres.
 */

interface PanelView {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Con qué prefijo de ruta se considera "la vista actual". */
  prefix: string;
}

const VIEWS: PanelView[] = [
  { to: "/admin", label: "Panel admin", icon: LayoutDashboard, prefix: "/admin" },
  { to: "/socio", label: "Panel socio", icon: UserRound, prefix: "/socio" },
  { to: "/mi-negocio", label: "Panel franquiciatario", icon: Store, prefix: "/mi-negocio" },
  { to: "/shop", label: "Tienda · vista cliente", icon: ShoppingBag, prefix: "/shop" },
];

export function PanelViewSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { open, toggle, close, triggerRef, panelRef, panelId } = useMenuPopover({
    focusSelector: '[role="menuitem"]',
  });

  const current =
    VIEWS.find(
      (view) => pathname === view.prefix || pathname.startsWith(`${view.prefix}/`),
    ) ?? VIEWS[0]!;
  const CurrentIcon = current.icon;

  return (
    <div className={cn("relative shrink-0", className)}>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="sm"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        leftIcon={<CurrentIcon />}
        className="h-9 border-ds-border bg-white/70"
      >
        <span className="hidden sm:inline">{t(current.label)}</span>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-200 motion-reduce:transition-none",
            open && "rotate-180",
          )}
        />
      </Button>

      {open && (
        <GlassPanel
          ref={panelRef}
          id={panelId}
          className="absolute right-0 top-full z-50 mt-2 w-[15.5rem] bg-white/85 p-2 shadow-ds-float"
        >
          <div role="menu" aria-label={t("Vista del panel")} className="relative grid gap-0.5">
            {VIEWS.map((view) => {
              const active = view.prefix === current.prefix;
              return (
                <button
                  key={view.to}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  onClick={() => {
                    close();
                    navigate(view.to);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-2xl px-2.5 py-2 text-left text-[13px] font-semibold",
                    "outline-none transition-colors duration-200 motion-reduce:transition-none",
                    active
                      ? "bg-ds-mist/70 text-ds-violet-pop"
                      : "text-ds-ink-3 hover:bg-ds-mist/70 hover:text-ds-violet-pop focus-visible:bg-ds-mist/70 focus-visible:text-ds-violet-pop",
                  )}
                >
                  <view.icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                  <span className="flex-1">{t(view.label)}</span>
                  {active && <Check aria-hidden="true" className="h-4 w-4 shrink-0" />}
                </button>
              );
            })}
          </div>
        </GlassPanel>
      )}
    </div>
  );
}
